import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { useTheme } from 'hooks/useTheme';

interface ContentPillProps {
  icon?: React.ReactNode;
  text: string;
  variant?: 'default' | 'outlined' | 'filled';
  size?: 'small' | 'medium' | 'large';
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  iconColor?: string;
  textColor?: string;
  onClick?: () => void;
  disabled?: boolean;
}

const ContentPill: React.FC<ContentPillProps> = ({
  icon,
  text,
  variant = 'outlined',
  size = 'medium',
  color,
  backgroundColor,
  borderColor,
  iconColor,
  textColor,
  onClick,
  disabled = false,
}) => {
  const theme = useTheme();
  const primary = theme.theme.primary;

  const defaultColors = {
    background: backgroundColor || (variant === 'outlined' ? primary : 'transparent'),
    border: borderColor || primary,
    text:
      textColor ||
      (variant === 'outlined' ? theme.theme.background.secondary : theme.theme.primary),
    icon: iconColor || (variant === 'outlined' ? theme.theme.background.secondary : primary),
  };

  const colors = {
    background: color || defaultColors.background,
    border: borderColor || defaultColors.border,
    text: textColor || defaultColors.text,
    icon: iconColor || defaultColors.icon,
  };

  const sizeConfig = {
    small: {
      padding: '2px 8px',
      fontSize: '0.75rem',
      iconSize: '0.875rem',
      borderRadius: '12px',
    },
    medium: {
      padding: '4px 12px',
      fontSize: '0.875rem',
      iconSize: '1rem',
      borderRadius: '16px',
    },
    large: {
      padding: '6px 16px',
      fontSize: '1rem',
      iconSize: '1.125rem',
      borderRadius: '20px',
    },
  };

  const config = sizeConfig[size];

  const pillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.2,
    padding: config.padding,
    fontSize: config.fontSize,
    borderRadius: config.borderRadius,
    border: `1px solid ${colors.border}`,
    backgroundColor: variant === 'outlined' ? colors.background : 'transparent',
    color: colors.text,
    cursor: onClick ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
    '&:hover':
      onClick && !disabled
        ? {
            backgroundColor: variant === 'outlined' ? colors.background : primary,
            transform: 'translateY(-1px)',
          }
        : {},
  };

  return (
    <Box sx={pillStyle} onClick={onClick && !disabled ? onClick : undefined}>
      {/*  {icon && (
        <Box
          sx={{
            color: colors.icon,
            fontSize: config.iconSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      )} */}
      <Typography
        sx={{
          fontSize: 'inherit',
          fontWeight: 500,
          lineHeight: 1.2,
          color: 'inherit',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

const MemoizedContentPill = React.memo(ContentPill);

// Componente para múltiplos pills em um container
interface PillsContainerProps {
  pills: Array<{
    icon?: React.ReactNode;
    text: string;
    variant?: 'default' | 'outlined' | 'filled';
    size?: 'small' | 'medium' | 'large';
    color?: string;
    backgroundColor?: string;
    borderColor?: string;
    iconColor?: string;
    textColor?: string;
    onClick?: () => void;
    disabled?: boolean;
  }>;
  direction?: 'row' | 'column';
  spacing?: number;
  wrap?: boolean;
}

export const PillsContainer: React.FC<PillsContainerProps> = ({
  pills,
  direction = 'row',
  spacing = 0.25,
  wrap = true,
}) => {
  return (
    <Stack
      direction={direction}
      spacing={spacing}
      sx={{
        flexWrap: wrap ? 'wrap' : 'nowrap',
        gap: spacing,
      }}
    >
      {pills.map((pill, index) => (
        <MemoizedContentPill
          key={index}
          icon={pill.icon}
          text={pill.text}
          variant={pill.variant}
          size={pill.size}
          color={pill.color}
          backgroundColor={pill.backgroundColor}
          borderColor={pill.borderColor}
          iconColor={pill.iconColor}
          textColor={pill.textColor}
          onClick={pill.onClick}
          disabled={pill.disabled}
        />
      ))}
    </Stack>
  );
};

export default MemoizedContentPill;
